import React, { useState } from 'react';
import { User, Users, Ghost, MapPin, Gavel, ArrowLeft, Trophy, Loader, ShieldCheck, Send, Search, Plus, X, Check } from 'lucide-react';

const OpponentSelectionModal = ({ isOpen, onClose, allProfiles, onSelectOpponent, currentOpponent }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTab, setSelectedTab] = useState('registered'); // 'registered' or 'guest'
    const [guestName, setGuestName] = useState('');

    const filteredProfiles = allProfiles.filter(profile => 
        profile.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelectRegistered = (profile) => {
        onSelectOpponent({
            name: profile.name,
            id: profile.uid,
            isProfile: true,
            profile: profile
        });
        onClose();
    };

    const handleSelectGuest = () => {
        if (guestName.trim()) {
            onSelectOpponent({
                name: guestName.trim(),
                id: null,
                isProfile: false,
                profile: null
            });
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-slate-700">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Select Opponent</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setSelectedTab('registered')}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                            selectedTab === 'registered' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                    >
                        <ShieldCheck className="w-4 h-4 inline mr-2" />
                        Registered Players
                    </button>
                    <button
                        onClick={() => setSelectedTab('guest')}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                            selectedTab === 'guest' 
                                ? 'bg-orange-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                    >
                        <User className="w-4 h-4 inline mr-2" />
                        Guest Player
                    </button>
                </div>

                {selectedTab === 'registered' ? (
                    <div>
                        {/* Search */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search registered players..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Registered Players List */}
                        <div className="max-h-64 overflow-y-auto space-y-2">
                            {filteredProfiles.length === 0 ? (
                                <div className="text-center py-8 text-slate-400">
                                    {searchTerm ? 'No players found matching your search.' : 'No registered players available.'}
                                </div>
                            ) : (
                                filteredProfiles.map(profile => (
                                    <button
                                        key={profile.uid}
                                        onClick={() => handleSelectRegistered(profile)}
                                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                            currentOpponent?.id === profile.uid
                                                ? 'bg-blue-600 border-blue-500 text-white'
                                                : 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <ShieldCheck className="w-4 h-4 text-green-400" />
                                                <span className="font-medium">{profile.name}</span>
                                            </div>
                                            {currentOpponent?.id === profile.uid && (
                                                <Check className="w-4 h-4" />
                                            )}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2 text-slate-300">
                                Guest Player Name
                            </label>
                            <input
                                type="text"
                                placeholder="Enter guest player name..."
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <button
                            onClick={handleSelectGuest}
                            disabled={!guestName.trim()}
                            className="w-full bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add Guest Player
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const SetupView = ({ 
    matchData, 
    setMatchData, 
    gameMode, 
    handleSendChallenge, 
    onBack,
    allProfiles,
    loadingProfiles,
    opponentProfile,
    setOpponentProfile,
    controlModeDisabled
}) => {
    const [showOpponentModal, setShowOpponentModal] = useState(false);
    
    const onFormSubmit = (e) => {
        e.preventDefault();
        handleSendChallenge();
    };

    const handleOpponentSelect = (opponent) => {
        setMatchData(prev => ({
            ...prev,
            player2: opponent.name,
            player2Id: opponent.id,
            player2IsProfile: opponent.isProfile,
        }));
        if (opponent.isProfile) {
            setOpponentProfile(opponent.profile);
        } else {
            setOpponentProfile(null);
        }
        setShowOpponentModal(false);
    };

    const handleGameTypeChange = (gameType) => {
        let updatedMatchData = { ...matchData, gameType };
        
        // Set game-specific defaults
        if (gameType === '8-Ball') {
            updatedMatchData = {
                ...updatedMatchData,
                callEight: true,        // Call 8-Ball should be selected by default
                threeFoulRule: false,   // 3-Foul Rule should be unselected by default
                eightOnBreakLoss: false // 8-Ball on Break is Loss should be unselected by default
            };
        } else if (gameType === '9-Ball') {
            updatedMatchData = {
                ...updatedMatchData,
                threeFoulRule: true,    // 3-Foul Rule should be selected by default for 9-Ball
                nineBallRule: 'wild',
                wildNineOnBreak: true
            };
        } else if (gameType === '10-Ball') {
            updatedMatchData = {
                ...updatedMatchData,
                threeFoulRule: true,    // 3-Foul Rule should be selected by default for 10-Ball
                tenBallLast: false
            };
        }
        
        setMatchData(updatedMatchData);
    };

    const getOpponentDisplay = () => {
        if (!matchData.player2 || matchData.player2 === 'Player 2') return 'Select opponent...';
        return (
            <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                    {matchData.player2IsProfile ? (
                        <ShieldCheck className="w-4 h-4 text-green-400" />
                    ) : (
                        <User className="w-4 h-4 text-orange-400" />
                    )}
                    <span>{matchData.player2}</span>
                </div>
                <span className="text-xs text-slate-400">
                    {matchData.player2IsProfile ? 'Registered' : 'Guest'}
                </span>
            </div>
        );
    };

    return (
        // --- MODIFICATION: Increased max-width for desktop view ---
        <div className="max-w-4xl mx-auto p-3 sm:p-6">
            <header className="flex items-center mb-4">
                <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2">
                    <ArrowLeft className="w-6 h-6" />
                </button>
            </header>
            <div className="text-center mb-6">
                <div className="bg-green-500/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
                    <Trophy className="w-10 h-10 text-green-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-2">Match Setup</h1>
                <p className="text-slate-400">Configure your game and start tracking</p>
            </div>

            <form onSubmit={onFormSubmit} className="bg-slate-800/50 rounded-xl p-4 sm:p-6 border border-slate-700">
                {/* --- MODIFICATION: Game mode selection moved to top and centered --- */}
                <div className="mb-8">
                    <label className="block text-sm font-medium mb-2 text-slate-300 text-center">Game Mode</label>
                    <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                        <button 
                            type="button" 
                            onClick={() => setMatchData(prev => ({ ...prev, gameMode: 'pvp' }))} 
                            className={`px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${gameMode === 'pvp' ? 'bg-green-500 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                        >
                            <Users size={18} /> Player vs Player
                        </button>
                        <button 
                            type="button" 
                            onClick={() => setMatchData(prev => ({ ...prev, gameMode: 'ghost' }))} 
                            className={`px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${gameMode === 'ghost' ? 'bg-purple-500 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                        >
                            <Ghost size={18} /> Player vs Ghost
                        </button>
                    </div>
                </div>

                {/* --- MODIFICATION: Main grid for two-column layout on large screens --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-8">
                    
                    {/* --- LEFT COLUMN: Core match details --- */}
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 text-slate-300"><User className="w-4 h-4 inline mr-2" /> Player Name</label>
                            <div className="flex items-center gap-2">
                                <input type="text" value={matchData.player1} disabled className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white placeholder-slate-400 disabled:opacity-70" />
                                {matchData.player1IsProfile && <ShieldCheck className="text-green-400" size={20} title="Registered Player" />}
                            </div>
                        </div>

                        {gameMode === 'pvp' && (
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">
                                    <User className="w-4 h-4 inline mr-2" /> Opponent
                                </label>
                                {loadingProfiles ? (
                                    <div className="flex items-center justify-center h-11 bg-slate-700 rounded-lg">
                                        <Loader className="animate-spin" size={20} />
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setShowOpponentModal(true)}
                                            className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-green-400 text-left"
                                        >
                                            {getOpponentDisplay()}
                                        </button>
                                        <p className="text-xs text-slate-400 mt-1">
                                            Click to select a registered player or add a guest player.
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-sm font-medium mb-2 text-slate-300">Game Type</label>
                            <div className="grid grid-cols-3 gap-2">
                                {['8-Ball', '9-Ball', '10-Ball'].map(type => (
                                    <button type="button" key={type} onClick={() => handleGameTypeChange(type)}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${matchData.gameType === type ? 'bg-green-500 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`} >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 text-slate-300">Race To</label>
                            {gameMode === 'pvp' ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium mb-1 text-slate-400 truncate">{matchData.player1}</label>
                                        <input type="number" value={matchData.player1RaceTo} onChange={(e) => setMatchData(prev => ({ ...prev, player1RaceTo: parseInt(e.target.value, 10) || 1 }))} className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-green-400" min="1" max="50" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1 text-slate-400 truncate">{matchData.player2}</label>
                                        <input type="number" value={matchData.player2RaceTo} onChange={(e) => setMatchData(prev => ({ ...prev, player2RaceTo: parseInt(e.target.value, 10) || 1 }))} className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-green-400" min="1" max="50" />
                                    </div>
                                </div>
                            ) : (
                                <input type="number" value={matchData.player1RaceTo} onChange={(e) => setMatchData(prev => ({ ...prev, player1RaceTo: parseInt(e.target.value, 10) || 1 }))} className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-green-400" min="1" max="50" />
                            )}
                        </div>

                         <div>
                            <label className="block text-sm font-medium mb-2 text-slate-300">
                                <MapPin className="w-4 h-4 inline mr-2" /> Location (Optional)
                            </label>
                            <input type="text" value={matchData.location}
                                onChange={(e) => setMatchData(prev => ({ ...prev, location: e.target.value }))}
                                className="w-full px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-400"
                                placeholder="e.g., Pool Hall, Home Table"
                            />
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: Game rules and settings --- */}
                    <div className="mt-6 lg:mt-0">
                        <div className="bg-slate-700/30 p-4 rounded-lg space-y-4 h-full border border-slate-700">
                            <h3 className="text-md font-semibold text-slate-300 flex items-center"><Gavel className="w-5 h-5 mr-2" />Game & Rule Settings</h3>
                            
                            {gameMode === 'pvp' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-slate-300">Control Mode</label>
                                    {controlModeDisabled ? (
                                        <p className="text-slate-400 bg-slate-700 rounded-lg px-3 py-2 text-sm">
                                            Control mode is disabled for guest opponents. Shared controls will be used.
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                            <button type="button" onClick={() => setMatchData(prev => ({ ...prev, controlMode: 'strict' }))}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${matchData.controlMode === 'strict' ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>
                                                Strict Turn-Based
                                            </button>
                                            <button type="button" onClick={() => setMatchData(prev => ({ ...prev, controlMode: 'shared' }))}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${matchData.controlMode === 'shared' ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>
                                                Shared Controls
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                             {gameMode === 'pvp' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-slate-300">Who Breaks First?</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" onClick={() => setMatchData(prev => ({ ...prev, firstBreaker: 'player1' }))} 
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all truncate ${matchData.firstBreaker === 'player1' ? 'bg-green-500 text-white shadow-lg' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>
                                            {matchData.player1}
                                        </button>
                                        <button type="button" onClick={() => setMatchData(prev => ({ ...prev, firstBreaker: 'player2' }))} 
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all truncate ${matchData.firstBreaker === 'player2' ? 'bg-green-500 text-white shadow-lg' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>
                                            {matchData.player2}
                                        </button>
                                    </div>
                                </div>
                            )}

                             {gameMode === 'pvp' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-slate-300">Break Format (After 1st Rack)</label>
                                    <select value={matchData.whoBreaks} onChange={(e) => setMatchData(prev => ({ ...prev, whoBreaks: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-slate-600 border border-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-green-400 appearance-none bg-no-repeat bg-right" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}>
                                        <option value="winner">Winner Breaks</option>
                                        <option value="alternate">Alternate</option>
                                    </select>
                                </div>
                            )}

                             {gameMode === 'pvp' && (
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-medium text-slate-300">3-Foul Rule</label>
                                    <button type="button" onClick={() => setMatchData(prev => ({ ...prev, threeFoulRule: !prev.threeFoulRule }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${matchData.threeFoulRule ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${matchData.threeFoulRule ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                                </div>
                            )}

                             <div>
                                <label className="block text-sm font-medium mb-2 text-slate-300">Rack Setup</label>
                                <select value={matchData.rackSetup} onChange={(e) => setMatchData(prev => ({ ...prev, rackSetup: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-600 border border-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-green-400 appearance-none bg-no-repeat bg-right" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}>
                                    <option value="standard">Standard</option>
                                    {matchData.gameType === '9-Ball' && <option value="nine_on_spot">9-Ball on the Spot</option>}
                                    {matchData.gameType === '10-Ball' && <option value="ten_on_spot">10-Ball on the Spot</option>}
                                </select>
                            </div>

                             {matchData.gameType === '9-Ball' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-slate-300">9-Ball Rule</label>
                                        <select value={matchData.nineBallRule} onChange={(e) => setMatchData(prev => ({ ...prev, nineBallRule: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-600 border border-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-green-400 appearance-none bg-no-repeat bg-right" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}>
                                            <option value="wild">Wild 9</option>
                                            <option value="call">Call 9</option>
                                        </select>
                                    </div>
                                    {matchData.nineBallRule === 'wild' && (
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-slate-300">9-Ball on Break Counts?</label>
                                            <button type="button" onClick={() => setMatchData(prev => ({ ...prev, wildNineOnBreak: !prev.wildNineOnBreak }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${matchData.wildNineOnBreak ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${matchData.wildNineOnBreak ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {matchData.gameType === '8-Ball' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-300">Call 8-Ball</label>
                                        <button type="button" onClick={() => setMatchData(prev => ({ ...prev, callEight: !prev.callEight }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${matchData.callEight ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${matchData.callEight ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-300">8-Ball on Break is Loss</label>
                                        <button type="button" onClick={() => setMatchData(prev => ({ ...prev, eightOnBreakLoss: !prev.eightOnBreakLoss }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${matchData.eightOnBreakLoss ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${matchData.eightOnBreakLoss ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                                    </div>
                                </div>
                            )}

                            {matchData.gameType === '10-Ball' && (
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-medium text-slate-300">10-Ball Must Be Made Last?</label>
                                    <button type="button" onClick={() => setMatchData(prev => ({ ...prev, tenBallLast: !prev.tenBallLast }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${matchData.tenBallLast ? 'bg-green-500' : 'bg-slate-600'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${matchData.tenBallLast ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                                </div>
                            )}
                            
                            <div>
                                <label className="block text-sm font-medium mb-2 text-slate-300">Tracking Mode</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => setMatchData(prev => ({ ...prev, trackingMode: 'detailed' }))} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${matchData.trackingMode === 'detailed' ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>Detailed</button>
                                    <button type="button" onClick={() => setMatchData(prev => ({ ...prev, trackingMode: 'simple' }))} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${matchData.trackingMode === 'simple' ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'}`}>Score Only</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- MODIFICATION: Submit button moved outside the grid for full width --- */}
                <div className="mt-8">
                    <button 
                        type="submit"
                        disabled={gameMode === 'pvp' && !matchData.player2.trim()}
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-green-700 transition-all flex items-center justify-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="w-5 h-5 mr-2" /> 
                        {opponentProfile ? 'Send Challenge' : 'Start Match'}
                    </button>
                </div>
            </form>

            <OpponentSelectionModal
                isOpen={showOpponentModal}
                onClose={() => setShowOpponentModal(false)}
                allProfiles={allProfiles}
                onSelectOpponent={handleOpponentSelect}
                currentOpponent={{
                    id: matchData.player2Id,
                    name: matchData.player2,
                    isProfile: matchData.player2IsProfile
                }}
            />
        </div>
    );
};
